import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { getCurrentProject, getProjectLoading, getProjects } from '@completion/selectors';
import { combineLatest, distinctUntilChanged, filter, map, Observable, take } from 'rxjs';

import { ChangeProject } from '../store/actions';

@Injectable({
  providedIn: 'root',
})
export class ProjectRouteRedirectGuard implements CanActivate {
  constructor(
    private readonly store: Store,
    private readonly router: Router,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const routeProjectIdParam = route.paramMap.get('projectId');
    const hasRouteProjectId = routeProjectIdParam !== null && /^\d+$/.test(routeProjectIdParam);
    const routeProjectId = hasRouteProjectId ? Number(routeProjectIdParam) : null;

    return combineLatest([this.store.select(getCurrentProject), this.store.select(getProjects), this.store.select(getProjectLoading)]).pipe(
      // CoreDataGuard runs before this guard and guarantees projects are loaded.
      // The filter below is belt-and-suspenders: it ensures we never act on the
      // initial empty state even if there is a microtask gap between the two guards.
      // distinctUntilChanged avoids re-logging/re-filtering on reference-only churn of the
      // selectors (e.g. ProjectsLoadSuccess/ChangeProject creating new object identities for
      // state that hasn't meaningfully changed), which was spamming the console and contributing
      // to main-thread pressure during the store-settle window.
      distinctUntilChanged((prev, curr) => prev[0]?.id === curr[0]?.id && prev[1].length === curr[1].length && prev[2] === curr[2]),

      filter(([currentProject, projects, isProjectsLoading]) => {
        const settled = !isProjectsLoading && (currentProject !== null || projects.length > 0);
        if (!settled) {
          console.warn('[ProjectRouteRedirectGuard] waiting for store to settle', { currentProject, projects, isProjectsLoading });
        }
        return settled;
      }),
      take(1),
      map(([currentProject, projects]) => {
        const activeProject = currentProject ?? projects[0] ?? null;

        // should not happen; CoreDataGuard already checked projects.length > 0
        if (!activeProject) {
          console.error('[ProjectRouteRedirectGuard] no active project after store settled – check CoreDataGuard', {
            currentProject,
            projects,
          });
          return this.router.parseUrl('/no-project-access');
        }

        if (hasRouteProjectId) {
          const selectedProject =
            projects.find((project) => project.id === routeProjectId) ?? (currentProject?.id === routeProjectId ? currentProject : null);

          // we need entire Project object in order to change project below (the ID alone is not sufficient),
          // so we might as well route proactively to error page (access denied) without waiting for the 401.
          // Otherwise, we'd end up trying to do ChangeProject(null)
          if (!selectedProject) {
            // Must be a non-project-scoped route; otherwise we re-enter this guard and can loop.
            return this.router.parseUrl('/access-denied');
          }

          if (currentProject?.id !== selectedProject.id) {
            this.store.dispatch(new ChangeProject(selectedProject));
          }

          window.localStorage.setItem('projectId', selectedProject.id.toString());
          return true;
        }

        if (!currentProject) {
          this.store.dispatch(new ChangeProject(activeProject));
        }

        window.localStorage.setItem('projectId', activeProject.id.toString());

        const rawTargetPath = state.url.startsWith('/') ? state.url : `/${state.url}`;
        const targetPath = rawTargetPath === '/' ? '/dashboard' : rawTargetPath;
        const projectScopedPath = `/${activeProject.id}${targetPath}`;
        return this.router.parseUrl(projectScopedPath);
      }),
    );
  }
}
